<!-- No lang attribute on this <template>: in a single-file component `lang`
     names a template PREPROCESSOR (pug, haml), not a human language. It was a
     no-op under vue-loader and a hard compile error under @vitejs/plugin-vue,
     which is what kept this view out of every unit test that mounts it. The
     page's language is declared by the locale watcher on <html lang>. -->
<template>
  <AppNavigation mode="topbar"/>
  <div class="main-intro">
    <div class="left-section">
      <h1 class="title u-display">{{ $t('about.title') }}</h1>
      <p class="content" style="white-space: pre-line">{{ $t('about.mission') }}</p>
    </div>
    <div class="about-us-hero">
      <img src="/img/about-us-hero.jpg" :alt="$t('about.heroAlt')" class="hero-image"/>
    </div>
  </div>
  <div class="founder-container">
    <div class="oval">
      <img src="@/assets/mathurshan.jpg" :alt="$t('about.founderPhotoAlt', { name: $t('about.founders.mathurshan.name') })">
      <h3>{{ $t('about.founders.mathurshan.name') }}</h3>
      <p>{{ $t('about.founders.mathurshan.bio') }}</p>
    </div>
    <div class="oval">
      <img src="@/assets/delu.jpg" :alt="$t('about.founderPhotoAlt', { name: $t('about.founders.delu.name') })">
      <h3>{{ $t('about.founders.delu.name') }}</h3>
      <p>{{ $t('about.founders.delu.bio') }}</p>
    </div>
    <div class="oval">
      <img src="@/assets/sean.png" :alt="$t('about.founderPhotoAlt', { name: $t('about.founders.sean.name') })">
      <h3>{{ $t('about.founders.sean.name') }}</h3>
      <p>{{ $t('about.founders.sean.bio') }}</p>
    </div>
  </div>
  <FooterSection/>
</template>

<script>
import AppNavigation from "@/components/AppNavigation.vue";
import FooterSection from "@/components/FooterSection.vue";
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { authSetupMixin } from "@/mixins/authSetupMixin.js";
import { publicPageHead } from "@/scripts/publicHead.js";

// Served from public/, not bundled from src/assets. This page's hero is also
// its og:image, and a webpack content hash in that URL 404s in crawler caches
// after every edit of the image. The build-time head injector cannot resolve a
// webpack hash at all, so the static and runtime heads can only agree on a
// stable path. src/data/publicRoutes.js declares the same path for /about-us.
const HERO_PATH = "/img/about-us-hero.jpg";

export default {
  components: { AppNavigation, FooterSection },
  name: "AboutUsView",
  mixins: [authSetupMixin],
  data() {
    return {
      socialImage: HERO_PATH,
    };
  },
  mounted() {
    // computed, not a plain string: locale bundles load lazily (Unit 2), so the
    // chosen locale's chunk can arrive after this view mounts. A string
    // evaluated here would freeze the English fallback into the tab and the
    // social card; a computed re-resolves when the bundle lands.
    useHead(
      publicPageHead({
        title: computed(() => this.$t("about.title")),
        description: computed(() => this.$t("about.metaDescription")),
        path: "/about-us",
        image: this.socialImage,
      }),
    );
  },
};
</script>

<style scoped>
.main-intro {
  margin-top: 100px;
  background: linear-gradient(120deg, var(--al-ink-900) 0%, var(--al-ink-700) 55%, var(--al-ink-600) 100%);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.left-section {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.title {
  padding: 20px;
  padding-top: clamp(30px, 5vw, 50px);
  color: var(--al-white);
  font-size: clamp(1.75rem, 4vw, 2.7rem);
  font-weight: bold;
  text-align: left;
}

.content {
  padding: 20px;
  color: var(--al-white);
  font-size: 16px;
  text-align: left;
  font-family: inherit;
  max-width: 680px;
}

.about-us-hero {
  padding: 20px;
  padding-top: 90px;
}

.hero-image {
  max-width: 70vh;
  height: auto;
  border-radius: 10px;
  mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 10%,
    rgba(0, 0, 0, 1) 49%,
    rgba(0, 0, 0, 0) 100%
  );
}

.founder-container {
  padding-top: 80px;
  padding-bottom: 30px;
  padding-left: 30px;
  padding-right: 30px;
  display: flex;
  justify-content: space-between;
}

.oval {
  flex: 0 0 30%;
  height: auto;
  text-align: center;
  margin-bottom: 20px;
}

.oval img {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  margin-bottom: 10px;
}

.oval h3 {
  margin: 0;
  padding-top: 20px;
  font-size: 25px;
}

.oval p {
  padding-top: 20px;
  margin: 0;
  font-size: 14px;
}

/* Styles for mobile screens */
@media (max-width: 767px) {
  .main-intro {
    flex-direction: column;
    background: var(--al-ink-900);
  }

  /* The fixed topbar grows by the safe-area inset on notched devices; the
     desktop 80px offset would sit under it. */
  .founder-container {
    padding-top: calc(var(--al-topbar-height) + 24px);
  }

  .about-us-hero {
    order: -1;
  }

  .hero-image {
    max-width: 90%;
  }

  .founder-container {
    flex-direction: column;
    align-items: center;
  }
}

/* Styles for large screens */
@media (min-width: 2000px) {
  .oval img {
    width: 400px;
    height: 400px;
  }

  .oval h3 {
    font-size: 50px;
  }

  .oval p {
    font-size: 25px;
  }
}
</style>
